import React, { forwardRef, useId } from 'react';
import { cn } from '../../utils/cn';

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  leftIcon?: React.ReactNode;
  rightSlot?: React.ReactNode;
  containerClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    id,
    label,
    hint,
    error,
    leftIcon,
    rightSlot,
    containerClassName,
    className,
    disabled,
    ...rest
  },
  ref
) {
  const reactId = useId();
  const inputId = id || `input-${reactId}`;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;

  return (
    <div className={cn('space-y-1.5', containerClassName)}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-body-sm font-medium text-neutral-700 dark:text-neutral-300"
        >
          {label}
        </label>
      )}
      <div className={cn('relative', leftIcon && 'has-left-icon')}>
        {leftIcon && (
          <span
            className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-neutral-400 dark:text-neutral-500"
            aria-hidden="true"
          >
            {leftIcon}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
          disabled={disabled}
          className={cn(
            'block w-full rounded-lg border bg-white dark:bg-neutral-900 px-3 py-2 text-body text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 placeholder:dark:text-neutral-500',
            'transition-colors duration-150 ease-out',
            'dark:bg-neutral-900 dark:text-neutral-100 dark:placeholder:text-neutral-500 dark:border-neutral-700',
            'focus:outline-none focus:ring-2 focus:ring-brand-600 focus:border-transparent',
            'disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:dark:bg-neutral-900/60 disabled:text-neutral-500 disabled:dark:text-neutral-400 dark:disabled:bg-neutral-800',
            leftIcon && 'pl-9',
            rightSlot && 'pr-10',
            error
              ? 'border-danger-400 focus:ring-danger-500 dark:border-danger-500'
              : 'border-neutral-300 dark:border-neutral-700',
            className
          )}
          {...rest}
        />
        {rightSlot && (
          <span className="absolute inset-y-0 right-0 flex items-center pr-3">
            {rightSlot}
          </span>
        )}
      </div>
      {hint && !error && (
        <p id={hintId} className="text-caption text-neutral-500 dark:text-neutral-400">
          {hint}
        </p>
      )}
      {error && (
        <p
          id={errorId}
          className="text-caption text-danger-600 dark:text-danger-400"
          role="alert"
          aria-live="polite"
        >
          {error}
        </p>
      )}
    </div>
  );
});

export default Input;
